/**
 * Apostille / MFA-legalisation Q&A — Wave 2 (deep-dive, self-contained answers).
 *
 * ACCURACY POLICY (ตรวจทาน 2026-08-04)
 * - ไทยยื่นภาคยานุวัติสารอนุสัญญา Apostille 30 มิ.ย. 2026 และอนุสัญญามีผลกับไทย
 *   28 ก.พ. 2027 → ปัจจุบันยังไม่มีตรา Apostille ของไทย (hcch.net · mfa.go.th
 *   · src/data/legal-facts.ts)
 * - เส้นทางปัจจุบัน: ต้นฉบับ/Notary → นิติกรณ์กองสัญชาติและนิติกรณ์ กรมการกงสุล
 *   → รับรองสถานทูตปลายทางเมื่อปลายทางกำหนด
 * - ค่าธรรมเนียมนิติกรณ์ 200 บาท/ตราประทับ (ปกติ) · 400 บาท/ตราประทับ (ด่วน)
 *   — consular.mfa.go.th
 * - ห้ามระบุราคาบริการของเรา · ห้ามให้คำมั่นเรื่องผลการพิจารณา · เวลาเป็นช่วงโดยประมาณ
 */
import type { ApostilleQA } from "./questions-500";
import { APOSTILLE_STATUS } from "@/data/legal-facts";

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — เงื่อนไขของหน่วยงานปลายทางและสถานทูตเปลี่ยนแปลงได้ โปรดยืนยันรายการเอกสารกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

const QUOTE =
  "ค่าบริการแจ้งเป็นรายเคสทางโทรศัพท์ LINE หรืออีเมล เพราะขึ้นกับจำนวนฉบับ ภาษา และชั้นการรับรองที่ปลายทางกำหนด ส่วนค่าธรรมเนียมราชการแสดงแยกเสมอ";

const FEE =
  "ค่าธรรมเนียมนิติกรณ์ของกรมการกงสุลคือ 200 บาทต่อตราประทับแบบปกติ และ 400 บาทต่อตราประทับแบบด่วน (consular.mfa.go.th)";

export const APOSTILLE_QA_WAVE2: ApostilleQA[] = [
  {
    slug: "apostille-w2-ไทยยังไม่ออกตราแล้วปลายทางขอมา",
    q: "ปลายทางขอ Apostille แต่ไทยยังไม่ออกตรา ต้องทำอย่างไร",
    aShort: `แจ้งปลายทางว่าเอกสารไทยยังใช้เส้นทางนิติกรณ์กรมการกงสุลและตราสถานทูต เพราะอนุสัญญาจะมีผลกับไทยวันที่ ${APOSTILLE_STATUS.inForceDateTh} แล้วขอให้ยืนยันเป็นลายลักษณ์อักษรว่ารูปแบบนี้ใช้ได้`,
    aLong: `เก็บอีเมลตอบกลับของหน่วยงานปลายทางไว้แนบกับชุดเอกสาร เพราะเจ้าหน้าที่ผู้รับเรื่องอาจไม่ใช่คนเดียวกับผู้ออกเช็กลิสต์ ${FEE} ${TAIL}`,
    synonyms: [
      "ปลายทางขอ apostille แต่ไทยยังไม่มี",
      "apostille alternative thailand 2026",
      "แทน apostille ไทย",
    ],
    cluster: "problem",
  },
  {
    slug: "apostille-w2-เส้นทางเอกสารทีละขั้น",
    q: "เส้นทางเอกสารไทยไปต่างประเทศมีกี่ขั้น",
    aShort:
      "โดยทั่วไปสี่ขั้น: คัดต้นฉบับจากหน่วยงานผู้ออก → แปลอังกฤษให้สะกดตรงพาสปอร์ต → นิติกรณ์ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล → รับรองสถานทูตปลายทางเมื่อปลายทางกำหนด",
    aLong: `เอกสารเอกชน เช่น หนังสือมอบอำนาจหรือ Affidavit ต้องเพิ่มขั้นรับรองลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารก่อนเข้าสายราชการ การสลับลำดับเป็นสาเหตุตีกลับที่พบบ่อยที่สุด เพราะการรับรองแต่ละชั้นอ้างอิงชั้นก่อนหน้า ${TAIL}`,
    synonyms: ["ขั้นตอนนิติกรณ์ทีละขั้น", "document chain thailand legalisation", "ลำดับรับรองเอกสาร"],
    cluster: "process",
  },
  {
    slug: "apostille-w2-นับตราประทับอย่างไร",
    q: "ค่าธรรมเนียมนิติกรณ์นับตราประทับอย่างไร",
    aShort:
      "นับเป็นรายตราประทับตามจำนวนฉบับหรือชุดที่ต้องรับรอง ไม่ได้คิดรวมต่อเคส จึงควรรวบรายการเอกสารให้ครบตั้งแต่รอบแรกเพื่อลดการเดินเรื่องซ้ำ",
    aLong: `${FEE} ถ้าต้องยื่นซ้ำเพราะคำแปลผิดหรือต้นฉบับไม่ครบ จะเสียค่าธรรมเนียมต่อตราประทับใหม่ทั้งชุด ${QUOTE} ${TAIL}`,
    synonyms: ["ค่าธรรมเนียมต่อตรา", "mfa legalisation fee per stamp", "นิติกรณ์คิดยังไง"],
    cluster: "price",
  },
  {
    slug: "apostille-w2-แบบด่วนกับแบบปกติ",
    q: "นิติกรณ์แบบด่วนต่างจากแบบปกติอย่างไร",
    aShort:
      "แบบด่วนมีค่าธรรมเนียมสูงกว่าและรอบให้บริการสั้นกว่า แต่ระยะเวลาจริงขึ้นกับคิวของหน่วยงานในช่วงนั้น จึงควรเผื่อเวลาและไม่ยึดวันนัดปลายทางเป็นเส้นตายเดียว",
    aLong: `${FEE} การยื่นช่วงเช้าช่วยให้ทันรอบของวันมากกว่าการยื่นช่วงบ่าย และการเตรียมต้นฉบับที่คัดใหม่ช่วยลดโอกาสถูกส่งกลับซึ่งกินเวลามากกว่าค่าธรรมเนียมด่วนที่ประหยัดได้ ${TAIL}`,
    synonyms: ["นิติกรณ์ด่วน", "express legalisation thailand", "ด่วนกี่วัน กรมการกงสุล"],
    cluster: "speed",
  },
  {
    slug: "apostille-w2-แปลก่อนหรือรับรองก่อน",
    q: "ต้องแปลก่อน หรือรับรองต้นฉบับก่อน",
    aShort:
      "แปลหลังจากได้ต้นฉบับที่คัดใหม่แล้ว จากนั้นนำต้นฉบับพร้อมคำแปลเข้าขั้นนิติกรณ์พร้อมกัน ถ้ารับรองต้นฉบับไปก่อนแล้วแปลทีหลัง คำแปลจะไม่อยู่ในชุดที่ถูกรับรอง",
    aLong: `ชื่อบุคคลในคำแปลต้องสะกดตรงกับพาสปอร์ตทุกตัวอักษร รวมถึงคำนำหน้าและลำดับชื่อกลาง เพราะผู้ตรวจเทียบทีละบรรทัดกับต้นฉบับ ${TAIL}`,
    synonyms: ["แปลก่อนไหม", "order translation legalisation thailand", "ลำดับแปลกับนิติกรณ์"],
    cluster: "process",
  },
  {
    slug: "apostille-w2-อายุเอกสารที่ปลายทางรับ",
    q: "ปลายทางกำหนดอายุเอกสารกี่เดือน",
    aShort:
      "ตราประทับไม่ระบุวันหมดอายุ แต่หน่วยงานปลายทางส่วนใหญ่ขอเอกสารสถานะบุคคลที่ออกหรือคัดใหม่ภายใน 3-6 เดือน จึงควรเริ่มงานใกล้กับวันยื่นจริง",
    aLong: `เอกสารที่มักถูกกำหนดอายุคือใบรับรองสถานะบุคคล ทะเบียนบ้าน และหนังสือรับรองความประพฤติ ส่วนวุฒิการศึกษาและทะเบียนสมรสมักไม่กำหนด แต่ต้องเป็นฉบับที่หน่วยงานผู้ออกคัดรับรองสำเนา ${TAIL}`,
    synonyms: ["เอกสารอายุกี่เดือน", "document validity legalisation", "ใบรับรองโสดอายุ"],
    cluster: "problem",
  },
  {
    slug: "apostille-w2-ห้ามถอดลวดเย็บ",
    q: "ทำไมห้ามถอดลวดเย็บชุดเอกสารที่รับรองแล้ว",
    aShort:
      "เจ้าหน้าที่เย็บชุดเอกสารเพื่อยืนยันว่าต้นฉบับกับคำแปลเป็นชุดเดียวกัน การถอดลวดเย็บหรือแยกหน้าทำให้ปลายทางถือว่าชุดเอกสารไม่สมบูรณ์และปฏิเสธได้",
    aLong: `ถ้าต้องถ่ายสำเนาให้ถ่ายโดยไม่แยกหน้า และถ้าต้องยื่นหลายหน่วยงานให้ขอรับรองจำนวนชุดตามที่ต้องใช้ตั้งแต่รอบแรก เพราะการขอรับรองใหม่คิดค่าธรรมเนียมต่อตราประทับใหม่ ${TAIL}`,
    synonyms: ["ถอดลวดเย็บได้ไหม", "do not unstaple legalised documents", "เอกสารรับรองแยกหน้า"],
    cluster: "problem",
  },
  {
    slug: "apostille-w2-เอกสารต่างชาตินำมาใช้ในไทย",
    q: "เอกสารจากต่างประเทศนำมาใช้ในไทยต้องทำอะไร",
    aShort:
      "ต้องผ่านการรับรองจากหน่วยงานของประเทศต้นทางและสถานทูตไทยหรือสถานทูตของประเทศนั้นในไทยตามที่กำหนด แล้วแปลเป็นภาษาไทยและยื่นรับรองคำแปลที่กรมการกงสุลก่อนใช้กับราชการไทย",
    aLong: `หน่วยงานผู้รับในไทยที่พบบ่อยคือสำนักงานเขตหรืออำเภอ กรมพัฒนาธุรกิจการค้า สำนักงานตรวจคนเข้าเมือง และศาล แต่ละแห่งมีเช็กลิสต์ของตนเอง จึงควรตรวจก่อนแปล ${FEE} ${TAIL}`,
    synonyms: [
      "เอกสารต่างประเทศใช้ในไทย",
      "foreign document use in thailand",
      "รับรองคำแปลไทย กรมการกงสุล",
    ],
    cluster: "inbound",
  },
  {
    slug: "apostille-w2-หลัง-2027-เปลี่ยนอะไร",
    q: `หลังวันที่ ${APOSTILLE_STATUS.inForceDateTh} อะไรจะเปลี่ยน`,
    aShort:
      "เอกสารที่จะใช้ในประเทศภาคีอนุสัญญาจะลดขั้นลงเหลือตรา Apostille ชั้นเดียว โดยไม่ต้องรับรองที่สถานทูตปลายทางอีก ส่วนประเทศที่ไม่ได้เป็นภาคียังใช้เส้นทางเดิม",
    aLong: `สิ่งที่ไม่เปลี่ยนคือความต้องการต้นฉบับที่คัดใหม่ คำแปลที่สะกดตรงพาสปอร์ต และการรับรองลายมือชื่อของเอกสารเอกชนโดยทนายความ ดังนั้นการเตรียมสองขั้นแรกไว้ล่วงหน้าใช้ได้กับทั้งเส้นทางเดิมและเส้นทางใหม่ ${TAIL}`,
    synonyms: [
      "apostille ไทยเริ่มเมื่อไหร่",
      "thailand apostille in force 2027",
      "หลัง apostille มีผล",
    ],
    cluster: "definition",
  },
  {
    slug: "apostille-w2-เอกสารบริษัทหลายฉบับ",
    q: "เอกสารบริษัทหลายฉบับควรจัดชุดอย่างไร",
    aShort:
      "แยกเป็นชุดตามวัตถุประสงค์และผู้รับ เพราะการรับรองคิดเป็นรายตราประทับ และปลายทางแต่ละแห่งอาจขอชุดต้นฉบับของตนเอง ไม่รับสำเนาที่ถ่ายจากชุดที่รับรองแล้ว",
    aLong: `เอกสารบริษัทที่ออกโดยกรมพัฒนาธุรกิจการค้าถือเป็นเอกสารราชการ เข้าสายนิติกรณ์ได้โดยตรง ส่วนมติที่ประชุมกรรมการ หนังสือมอบอำนาจ และสัญญา เป็นเอกสารเอกชน ต้องผ่านการรับรองลายมือชื่อโดยทนายความก่อน ${QUOTE} ${TAIL}`,
    synonyms: [
      "เอกสารบริษัทรับรองต่างประเทศ",
      "company documents legalisation thailand",
      "หนังสือรับรองบริษัทใช้ต่างประเทศ",
    ],
    cluster: "documents",
  },
  {
    slug: "apostille-w2-ส่งเอกสารต่างจังหวัด",
    q: "อยู่ต่างจังหวัดหรือต่างประเทศ ดำเนินการได้ไหม",
    aShort:
      "ได้ในขั้นเตรียมและแปล โดยส่งไฟล์สแกนคุณภาพสูงมาตรวจก่อน แต่ขั้นรับรองต้องใช้ต้นฉบับตัวจริง จึงต้องจัดส่งเอกสารเข้ามาตามรอบที่นัดไว้",
    aLong: `กรณีผู้ลงนามอยู่ต่างประเทศและต้องรับรองลายมือชื่อ ต้องลงนามต่อหน้าเจ้าหน้าที่ที่มีอำนาจตามที่ปลายทางยอมรับ เช่น สถานเอกอัครราชทูตหรือสถานกงสุลไทย ${QUOTE} ${TAIL}`,
    synonyms: [
      "ส่งเอกสารทางไปรษณีย์รับรอง",
      "legalisation from upcountry thailand",
      "อยู่ต่างประเทศรับรองเอกสารไทย",
    ],
    cluster: "process",
  },
  {
    slug: "apostille-w2-ตรวจเช็กลิสต์ปลายทางอย่างไร",
    q: "ตรวจเช็กลิสต์ของปลายทางให้ชัวร์ต้องดูอะไร",
    aShort:
      "ดูสี่จุด: ภาษาของคำแปลที่ยอมรับ ผู้แปลที่ยอมรับ ชั้นการรับรองที่ต้องมี และอายุเอกสารที่กำหนด จากหน้าเว็บทางการของหน่วยงานปลายทางหรือสถานทูตเท่านั้น",
    aLong: `ควรบันทึกวันที่อ่านเช็กลิสต์และเก็บภาพหน้าจอไว้ เพราะข้อกำหนดปรับได้ตลอด และเมื่อยื่นจริงเจ้าหน้าที่จะยึดตามฉบับที่บังคับใช้ในวันนั้น หลีกเลี่ยงการยึดข้อมูลจากกลุ่มสนทนาหรือประสบการณ์ของผู้อื่นเป็นหลัก ${TAIL}`,
    synonyms: [
      "อ่านเช็กลิสต์สถานทูต",
      "embassy checklist thailand documents",
      "ยืนยันข้อกำหนดปลายทาง",
    ],
    cluster: "process",
  },
];
